// Minimal history-API router: enough for a handful of screens, no dependency.

import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { flushSync } from 'react-dom';

const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Going deeper slides the new screen in from the right; the in-app back button reverses it. */
export type Transition = 'forward' | 'back' | 'none';

let backPending = false;

/** Swap screens inside a view transition, where the browser has them (Safari 18+). */
function withTransition(direction: Transition, update: () => void): void {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => { finished: Promise<void> } };
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (direction === 'none' || !doc.startViewTransition || reduce) {
    update();
    return;
  }
  document.documentElement.dataset.nav = direction;
  const t = doc.startViewTransition(() => flushSync(update));
  t.finished.finally(() => delete document.documentElement.dataset.nav);
}

// Back returns to where you were on the list: each history entry keeps its scroll position,
// and the screen renders in full (synchronously) before it's put back. The browser's own
// restoration runs before the old screen re-renders, so it lands short and then jumps.
let lastNav: 'push' | 'pop' = 'push';

/** True while the screen on show came from Back, so it can reopen what was open. */
export function cameBack(): boolean {
  return lastNav === 'pop';
}

/** Pages that open on a layer over the page below (which keeps its scroll); set by the app. */
let isLayer: (path: string) => boolean = () => false;
export function setLayerRoutes(test: (path: string) => boolean): void {
  isLayer = test;
}

function rememberScroll(): void {
  history.replaceState({ ...(history.state ?? {}), scrollY: window.scrollY }, '');
}

if (typeof window !== 'undefined') {
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  // iOS's own swipe-back already animates, so only the in-app back button gets the slide.
  window.addEventListener('popstate', () => {
    const direction: Transition = backPending ? 'back' : 'none';
    backPending = false;
    lastNav = 'pop';
    const y = (history.state as { scrollY?: number } | null)?.scrollY ?? 0;
    withTransition(direction, () => {
      flushSync(notify);
      window.scrollTo(0, y);
    });
  });
}

/** In the preview build the app lives under a sub-path; keep routes relative to it. */
const BASE = (() => {
  if (typeof document === 'undefined') return '';
  const b = document.querySelector('base')?.getAttribute('href');
  if (!b || b === '/' || b.startsWith('.')) return '';
  return b.replace(/\/$/, '');
})();

function currentPath(): string {
  if (typeof window === 'undefined') return '/';
  // Hash routes are used where a server can't fall back to index.html (the preview page).
  if (window.location.hash.startsWith('#/')) return window.location.hash.slice(1);
  const p = window.location.pathname.slice(BASE.length) || '/';
  return p;
}

export const useHashRouting = typeof window !== 'undefined' && (import.meta.env.MODE === 'preview' || window.location.protocol === 'file:');

export function usePath(): string {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    currentPath,
    () => '/',
  );
}

export function navigate(to: string, opts: { replace?: boolean; transition?: Transition } = {}): void {
  const url = useHashRouting ? `#${to}` : BASE + to;
  lastNav = 'push';
  if (opts.replace) history.replaceState({}, '', url);
  else {
    rememberScroll();
    history.pushState({}, '', url);
  }
  withTransition(opts.transition ?? 'none', () => {
    notify();
    // A layer scrolls on its own, from the top; the page under it stays where it was.
    if (!isLayer(to)) window.scrollTo(0, 0);
  });
}

export function back(fallback: string): void {
  if (history.length > 1 && (history.state !== null || document.referrer.startsWith(location.origin))) {
    backPending = true;
    history.back();
  } else navigate(fallback, { replace: true, transition: 'back' });
}

/** Match "/trips/:tripId/:segId" against a path; returns params or null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('?')[0].split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(a[i]);
    else if (p[i] !== a[i]) return null;
  }
  return params;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean; transition?: Transition };

export function Link({ to, replace, transition = 'forward', onClick, ...rest }: LinkProps) {
  const href = useHashRouting ? `#${to}` : BASE + to;
  return (
    <a
      {...rest}
      href={href}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to, { replace, transition });
      }}
    />
  );
}
